/**
 * useTabletSlaStrip — live Tier-2 approval-SLA count for the tablet idle
 * dashboard (Plan 02-17 Task 1 / INV-SLA-3).
 *
 * The kitchen tablet's idle dashboard surfaces an ochre strip at the very
 * top whenever any awaiting-approval row has been escalated past Tier-2 (24h
 * since `completed_at`). The count is server-authoritative: the
 * approvalSlaNudge job (Plan 02-06) recomputes it after every sweep and
 * broadcasts `{topic:'chores', event:'sla_count_changed', data:{n}}` on the
 * /ws hub. This hook subscribes to that broadcast and reflects the count
 * locally.
 *
 * Initial count on mount: best-effort fetch of /api/chores/awaiting-approval
 * filtered for `second_nudge_sent_at != null`. The endpoint is admin-only,
 * so on the typical authless kitchen-tablet the fetch returns 401 — we
 * swallow that silently and rely on the next WS broadcast to seed the
 * value. On admin sessions (a parent's phone), the fetch succeeds and the
 * count is correct immediately.
 *
 * Pattern mirrors useChoresWS (Plan 02-09): raw WebSocket, exponential
 * backoff reconnect, ignore unknown verbs, drop malformed frames silently.
 * Distinct hook (rather than reusing useChoresWS) so the tablet idle
 * dashboard does not need TanStack Query cache invalidation — it owns a
 * tiny piece of local state used only by ApprovalSlaStrip.
 */
import { useEffect, useState } from 'react';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

type AwaitingApprovalRow = {
  status?: string;
  second_nudge_sent_at?: string | null;
};

export function useTabletSlaStrip(): { count: number } {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // Initial seed — admin-authed clients get the live count, authless
    // kitchen tablet falls through silently.
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/chores/awaiting-approval', {
          credentials: 'same-origin',
        });
        if (!r.ok) return;
        const list = (await r.json()) as AwaitingApprovalRow[];
        if (cancelled) return;
        const n = Array.isArray(list)
          ? list.filter((row) => row?.second_nudge_sent_at != null).length
          : 0;
        setCount(n);
      } catch {
        // Network blip / shape mismatch — leave count at 0; WS will seed.
      }
    })();

    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const msg = JSON.parse(String(data)) as {
          topic?: string;
          event?: string;
          data?: { n?: number };
        };
        if (msg?.topic !== 'chores') return;
        if (msg.event !== 'sla_count_changed') return;
        const n = msg.data?.n;
        if (typeof n === 'number' && Number.isFinite(n) && n >= 0) {
          setCount(Math.floor(n));
        }
      } catch {
        // Drop malformed frames silently — Plan 05 server-side validation
        // means anything malformed reaching us is stray noise.
      }
    };

    const dispose = openReconnectingSocket({ topics: ['chores'], onMessage });

    return () => {
      cancelled = true;
      dispose();
    };
  }, []);

  return { count };
}
